export type Badge = { tag?: string; value?: string };

export type TagsConfig = Record<
  string,
  {
    enabled?: boolean;
    location?: string;
    priority?: number;
    parameters?: { colorText?: string; color?: string; url?: string };
  }
>;

type MetaResponse = { badges?: { tags?: TagsConfig } };

export type FetchJson = <T>(url: string) => Promise<T | null>;

const PERCENTAGE_OFF_TAG = "percentage-off-sale";
const SPECIAL_ORDER_TAG = "special-order";
const UNKNOWN_LOCATION_RANK = 10;
const DEFAULT_PRIORITY = 100;

const cachedTagsConfigBySiteId = new Map<string, TagsConfig | null>();

export async function fetchTagsConfig(
  siteId: string,
  fetchJson: FetchJson,
): Promise<TagsConfig | null> {
  const normalizedSiteId = siteId;
  if (!normalizedSiteId) return null;

  const cached = cachedTagsConfigBySiteId.get(normalizedSiteId);
  if (cached !== undefined) return cached;

  const url = `https://${normalizedSiteId}.a.searchspring.io/api/meta/meta?siteId=${normalizedSiteId}`;

  let data: MetaResponse | null;
  try {
    data = await fetchJson<MetaResponse>(url);
  } catch {
    return null;
  }

  const rawTagsConfig = data?.badges?.tags;
  const tagsConfig =
    rawTagsConfig && typeof rawTagsConfig === "object" && !Array.isArray(rawTagsConfig)
      ? rawTagsConfig
      : null;

  cachedTagsConfigBySiteId.set(normalizedSiteId, tagsConfig);
  return tagsConfig;
}

function getLocationRank(location: string | undefined): number {
  if (location === "callout") return 0;
  if (location === "left") return 1;
  if (location === "right") return 2;
  return UNKNOWN_LOCATION_RANK;
}

export function renderBadges(
  containerElement: HTMLElement,
  badges: Badge[],
  tagsConfig: TagsConfig | null,
): void {
  const normalizedBadges = badges.map((badge, index) => ({
    index,
    tag: typeof badge.tag === "string" ? badge.tag.trim() : "",
    value: typeof badge.value === "string" ? badge.value.trim() : "",
  }));

  const hasPercentageOff = normalizedBadges.some(
    (badge) => badge.tag === PERCENTAGE_OFF_TAG,
  );

  const filteredBadges = normalizedBadges.filter((badge) => {
    if (!badge.tag && !badge.value) return false;
    if (badge.tag === "sale" && hasPercentageOff) return false;
    if (badge.tag === SPECIAL_ORDER_TAG) return false;
    if (badge.value.toLowerCase().includes("special order")) return false;
    if (!tagsConfig || !badge.tag) return true;
    return tagsConfig[badge.tag]?.enabled !== false;
  });

  if (!filteredBadges.length) return;

  if (tagsConfig) {
    filteredBadges.sort((leftBadge, rightBadge) => {
      const leftConfig = tagsConfig[leftBadge.tag];
      const rightConfig = tagsConfig[rightBadge.tag];

      const leftLocationRank = getLocationRank(
        leftConfig?.location?.split("/")[0],
      );
      const rightLocationRank = getLocationRank(
        rightConfig?.location?.split("/")[0],
      );
      if (leftLocationRank !== rightLocationRank) {
        return leftLocationRank - rightLocationRank;
      }

      const leftPriority =
        typeof leftConfig?.priority === "number"
          ? leftConfig.priority
          : DEFAULT_PRIORITY;
      const rightPriority =
        typeof rightConfig?.priority === "number"
          ? rightConfig.priority
          : DEFAULT_PRIORITY;
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return leftBadge.index - rightBadge.index;
    });
  }

  const fragment = document.createDocumentFragment();

  for (const badge of filteredBadges) {
    const { tag, value } = badge;

    const badgeWrapper = document.createElement("div");
    const wrapperClasses = ["card__badge", "top"];

    if (tagsConfig && tag) {
      const location = tagsConfig[tag]?.location;
      const locationKey = location?.split("/")[0] ?? "";
      if (locationKey === "right") wrapperClasses.push("right");
    }

    badgeWrapper.className = wrapperClasses.join(" ");

    const badgeLabel = document.createElement("span");
    badgeLabel.className = tagsConfig ? "ss-pdp-badge" : "ss-pdp-badge-default";

    const parameters = tagsConfig?.[tag]?.parameters;
    if (parameters?.colorText) badgeLabel.style.color = parameters.colorText;
    if (parameters?.color) badgeLabel.style.backgroundColor = parameters.color;

    const imageUrl = parameters?.url;
    if (imageUrl) {
      const image = document.createElement("img");
      image.className = "ss-pdp-badge-img";
      image.src = imageUrl;
      image.alt = value || tag;
      badgeLabel.textContent = "";
      badgeLabel.append(image);
    } else {
      badgeLabel.textContent =
        tag === PERCENTAGE_OFF_TAG && value ? `${value}` : value || tag;
    }

    badgeWrapper.append(badgeLabel);
    fragment.append(badgeWrapper);
  }

  containerElement.classList.add("card__badges");
  if (!tagsConfig) containerElement.classList.add("badges", "badges--top-left");
  containerElement.append(fragment);
}
